import { DIFFICULTY } from '@/config/difficulty';
import { LOOT } from '@/config/loot';
import { PLAYER } from '@/config/player';
import { SPAWNS } from '@/config/waves';
import { Rng, freshSeed } from '@/core/rng';
import { clearSave, loadRun, saveRun, setActiveSlot } from '@/core/save';
import { LAYOUTS } from '@/arena/layouts';
import { BOSS_POOL } from '@/bosses/registry';
import type { World } from '@/scene/world';
import type { RecordToasts } from '@/ui/record-toasts';
import type { TitleScreen } from '@/ui/title-screen';
import type { UpgradeScreen } from '@/ui/upgrade-screen';
import { music } from '@/audio/music';
import { spawnRoomBoss } from './boss-spawn';
import { buildSave, restoreRun } from './run-save';
import type { RunSetup, RunState } from './run-state';
import { chamberFor, layoutIndexFor, themeFor } from './sector-hooks';
import { rebuildFromCards } from './ship';
import type { ShopFlow } from './shop-flow';
import type { WaveDirector } from './wave-director';
import { roomHash } from './wave-plan';

/** Hash salt of a room's decor stream (visual only — never the run rng) */
const DECOR_SALT = 911;

/**
 * Run lifecycle (docs/01): a new run (sector or endless) / continue from a
 * slot, room loading, leaving the shop for the next room (or the endless
 * death retry), autosave. Sector victory/defeat: run/sector-flow.ts.
 */
export class RunFlow {
  /** False during the boot's silent first arena load (no music yet) */
  booted = false;
  /** How the current run started (retry / next sector reuse it) */
  lastSetup: RunSetup | null = null;

  constructor(
    private readonly world: World,
    private readonly run: RunState,
    private readonly waves: WaveDirector,
    private readonly shop: ShopFlow,
    private readonly shopScreen: UpgradeScreen,
    private readonly title: TitleScreen,
    private readonly toasts: RecordToasts,
    /** ?seed= URL pin (null = random per run) */
    private readonly pinnedSeed: number | null,
  ) {}

  /**
   * Title → a fresh run in `slot` (a sector start is ALWAYS a fresh build:
   * starting weapon, no cards, no gems — docs/01). Seed priority: title
   * picker > ?seed= > random.
   */
  startRun(slot: number, setup: RunSetup): void {
    const { world, run } = this;
    this.title.hide();
    this.lastSetup = { ...setup };
    setActiveSlot(slot);
    clearSave();
    run.resetForNewGame(setup.seed ?? this.pinnedSeed ?? freshSeed(), setup);
    world.player.resetRun();
    world.player.setEquipped(run.inventory, run.inventory);
    // Fresh runs start wounded (docs/12 L2); later sectors at their own hull
    world.player.hp = run.sector ? DIFFICULTY.sectors[run.sectorIndex]!.startHp : PLAYER.startHp;
    world.bullets.friendlyFired = 0;
    this.toasts.reset();
    this.loadArena(layoutIndexFor(run, 0, 0));
  }

  /** Resume a slot: rebuild the ship from the saved collection (docs/01). */
  continueGame(slot: number): void {
    const { world, run } = this;
    setActiveSlot(slot);
    this.lastSetup = null;
    const save = loadRun();
    this.title.hide();
    if (!save) return;
    const ship = restoreRun(run, save, BOSS_POOL[0]!.def.id);
    world.bullets.friendlyFired = ship.bulletsFired;
    rebuildFromCards(world.player, run, ship.equippedIds);
    world.player.hp = Math.min(ship.hp, world.player.maxHp);
    world.player.armor = ship.armor;

    // A death checkpoint drops straight back into the FAILED room at its
    // wave (X11); a shop-break save reopens the shop over the cleared
    // arena (its fight replayed as a corpse — no fresh draw, no wasted rung)
    this.loadArena(save.arenaIndex, true);
    if (ship.inFight) return;
    if (world.boss) {
      world.boss.dead = true;
      world.setBossVisible(false);
    }
    this.shop.open();
  }

  /** Shop CONTINUE: next room — or, from the death bay, the SAME room again. */
  continueRun(): void {
    const run = this.run;
    this.shopScreen.hide();
    if (run.deathShop) {
      // Post-death bay (docs/12 #6): re-enter the SAME arena, fresh full-HP boss
      run.deathShop = false;
      run.nextPick = null; // clear the retry-preview so the next shop re-picks
      this.loadArena(run.arenaIndex, true);
      this.save();
    } else {
      run.roomNumber++;
      this.loadArena(layoutIndexFor(run, run.roomNumber, run.arenaIndex + 1));
    }
  }

  /** Autosaved at every shop break and after each transaction (docs/01). */
  save(): void {
    const player = this.world.player;
    saveRun(buildSave(this.run, {
      equippedIds: player.weapon.equipped,
      hp: player.hp,
      armor: player.armor,
      bulletsFired: this.world.bullets.friendlyFired,
    }, this.shopScreen.open));
  }

  /**
   * Build the room: theme + leveled boss + waves. Portal starts closed.
   * ARENA rooms spawn the ship dead-centre; ADVANCE rooms at the far end
   * of segment 1, with the boss waiting in a chamber sized for it.
   */
  loadArena(index: number, retrySameBoss = false): void {
    const { world, run } = this;
    run.arenaIndex = index % LAYOUTS.length;
    const layout = LAYOUTS[run.arenaIndex]!;
    const room = run.roomNumber;
    const pick = retrySameBoss ? run.currentFight : run.nextPick;
    const decor = new Rng(roomHash(run.seed, room, DECOR_SALT));
    const arena = world.buildArena(decor, layout, themeFor(run, room), chamberFor(pick?.id));
    run.lootThresholds = [...LOOT.shieldThresholds];

    // Everything else keeps clear of the ship's spawn
    const spawn = arena.spawnPoint();
    const player = world.player;
    player.spawnAt(spawn.x, spawn.y);
    world.camera.snapTo(spawn.x, spawn.y);

    // Arena guards (docs/12 M9): pushed off rocks AND away from the player
    // so they can actually be reached + shot back (turret-lock fix). Route
    // segments field theirs when the segment's fight starts.
    if (!arena.isRoute) {
      for (const tp of arena.turrets[0] ?? []) {
        const p = arena.clearPoint(tp.x, tp.y, spawn.x, spawn.y, SPAWNS.turret.away, SPAWNS.turret.clear);
        world.minions.spawn('turret', 1, p.x, p.y);
      }
    }
    // Phase Shield card: every arena starts with armor units already up
    player.armor = Math.max(player.armor, player.stats.shieldStart);
    // Nano Swarm card: arena-start hull repair (one cell per stack)
    if (player.stats.regenCells > 0) {
      player.hp = Math.min(player.maxHp, player.hp + player.stats.regenCells * PLAYER.cellHp);
    }

    spawnRoomBoss(world, run, retrySameBoss, spawn);
    world.telegraphs.clear();
    this.waves.beginRoom(retrySameBoss);

    const portalPos = arena.safePoint(SPAWNS.portal[0], SPAWNS.portal[1]);
    world.portal.place(portalPos.x, portalPos.y);
    world.portal.setActive(false);

    // Fresh random combat track per arena, crossfaded (user request)
    if (this.booted) music.playCombat();
  }
}
